<script setup>
import { reactive, computed, ref, onMounted } from "vue";

import { VIcon } from "@astrocentro-webapp/ui/modules/commons/components/VIcon";
import { VModal } from "@astrocentro-webapp/ui/modules/commons/components/VModal";
import { VYoutubeVideo } from "@astrocentro-webapp/ui/modules/commons/components/VYoutubeVideo";
import { SellerAvatar } from "@astrocentro-webapp/ui/modules/sellers/components/SellerAvatar";
import { getFormattedPrice } from "@astrocentro-webapp/commons/modules/commons/utils/getFormattedPrice";
import { SellerPresentationCardGhost } from "./index";
import { stars } from "./data";

let state = reactive({
  isLoading: true,
});

const emit = defineEmits(["has-mounted"]);

const props = defineProps({
  seller: {
    type: Object,
  },
  hasPromo: {
    type: Boolean,
  },
  trackerId: {
    type: String,
  },
  ctaOptions: {
    type: Object,
    default() {
      return {
        text: null,
        color: null,
      };
    },
  },
  extraParams: {
    type: String,
    default: "",
  },
  targetAttribute: {
    type: String,
    default: "_self",
  },
  sectionName: {
    type: String,
    default: "",
  },
});

const open = ref(false);

const rating = computed(() => {
  const rawRating = Math.round(props.seller.rating) / 10;
  return rawRating <= 9.9 ? Math.floor(rawRating) : Math.round(rawRating);
});

const isUnavailable = computed(() => props.seller.status === "unavailable");

const sellerExternalUrl = computed(() => {
  if (props.trackerId) {
    return `${props.seller.externalUrl}?tracker_id=${props.trackerId}&${props.extraParams}`;
  }

  return (
    props.seller.externalUrl +
    `${props.extraParams ? `?${props.extraParams}` : ""}`
  );
});

const sellersStatus = computed(() => {
  if (isSellerBusy()) {
    return "busy";
  }
  return props.seller.status;
})

const getStatusText = () => {
  if (isUnavailable.value) {
    return "Avise-me";
  }
  if (props.hasPromo) {
    return "Consulte valor promocional";
  }
  if (props.ctaOptions?.text) {
    return props.ctaOptions?.text;
  }
  return "Consulte";
};

const getButtonVariant = () =>
  isUnavailable.value ? "outline-primary shadow-sm" : "primary";

const getHasPromoPrice = () =>
  props.hasPromo ? "text-decoration-line-through" : "";

const isSellerBusy = () =>
    props.seller?.services.services.find(
        x => x.service === 'chat-consultation' && x.status === 'unavailable' && x.connected === true ||
            x.service === 'phone-consultation' && x.status === 'unavailable' && x.connected === true
    )
const handleClickLink = (event, url) => {
  event.preventDefault();

  const item = {
    item_id: props.seller.externalId,
    item_name: props.seller.name,
    affiliation: "Astrocentro",
    coupon: props.trackerId ? props.trackerId : "",
    discount: 0,
    index: 0,
    item_brand: props.seller.name,
    item_category: props.seller.positioning,
    item_category2: null,
    item_list_id: "especialistas",
    item_list_name: props.sectionName,
    item_variant: null,
    location_id: null,
    price: getFormattedPrice(props.seller.price),
    quantity: 1,
  };
  const twoHoursInSeconds = 60 * 60 * 2;
  const stalseCookie = `stalse=${encodeURIComponent(
    JSON.stringify(item)
  )}; domain=${window.location.host}; path=/; max-age=${twoHoursInSeconds};`;

  document.cookie = stalseCookie;
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: "select_item",
    ecommerce: {
      item_list_id: "especialistas",
      item_list_name: props.sectionName,
      items: [item],
    },
  });

  if (props.targetAttribute === "_blank") {
    window.open(url, props.targetAttribute);

    return;
  }

  window.location.href = url;
};

onMounted(() => {
  setTimeout(() => {
    state.isLoading = false;
    emit("has-mounted");
  }, 1000);
});
</script>

<template>
  <div class="card shadow-sm">
    <div v-if="state.isLoading" class="card-body">
      <SellerPresentationCardGhost />
    </div>
    <div v-else class="card-body">
      <div class="row card-body-height">
        <div class="col-4 pe-3 text-center">
          <SellerAvatar
            size="md"
            :variant="sellersStatus"
            :src="props.seller.thumbnail"
            :externalUrl="sellerExternalUrl"
            v-slot="{ variants }"
            :targetAttribute="props.targetAttribute"
          >
            <span
              :class="`text-white rounded-pill text-capitalize mt-2 fs-10 py-1 px-2 ${
                variants[sellersStatus].class
              }`"
            >
              {{ variants[sellersStatus].name }}
            </span>
          </SellerAvatar>
        </div>
        <div class="col-8 ps-0">
          <a
            class="text-decoration-none"
            :href="sellerExternalUrl"
            :target="props.targetAttribute"
            @click="handleClickLink($event, sellerExternalUrl)"
          >
            <h6
              class="col-11 text-truncate text-capitalize text-dark fw-semibold mb-0 fs-8 lh-small"
            >
              {{ props.seller.name }}
            </h6>
          </a>
          <div class="col-11 text-soft-light mb-3 text-truncate fs-8 lh-small">
            {{ props.seller.positioning }}
          </div>
          <div class="d-flex gap-2 mb-2" v-if="rating">
            <VIcon
              v-for="(star, index) of stars[rating]"
              color="yellow"
              :key="index"
              :icon="star"
              :width="18"
              :height="18"
            />
          </div>
          <small class="d-block mb-2 fw-semibold" v-else
            >Especialista novo</small
          >
          <small class="d-block mb-3 lh-1" v-if="!isUnavailable">
            ({{ props.seller.numberOfTransactions }} consultas)
          </small>
          <div
            class="d-inline-flex gap-2 align-items-center mb-3"
            role="button"
            tabindex="0"
            v-if="props.seller.profileVideo && !isUnavailable"
            @click="open = true"
          >
            <small class="text-primary fw-semibold">Assistir vídeo</small>
            <VIcon
              class="d-flex align-items-center"
              icon="play-circle"
              :width="18"
              :height="18"
            />
          </div>
          <p
            class="d-block fw-semibold mb-3 card-price"
            :class="getHasPromoPrice()"
          >
            a partir de {{ props.seller.price }}/min
          </p>
        </div>
      </div>
      <div class="d-grid">
        <a
          :href="sellerExternalUrl"
          :class="`btn btn-${getButtonVariant()}`"
          role="button"
          tabindex="0"
          :style="[
            props.ctaOptions.color
              ? {
                  backgroundColor: `#${props.ctaOptions.color} !important`,
                  borderColor: 'initial !important',
                }
              : {},
          ]"
          :target="props.targetAttribute"
          @click="handleClickLink($event, sellerExternalUrl)"
        >
          {{ getStatusText() }}
        </a>
      </div>
    </div>
    <VModal :open="open" id="seller-video-modal" @modal-closed="open = false">
      <div class="position-relative">
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="seller-video-modal"
          aria-label="Fechar"
          @click="open = false"
        ></button>
      </div>
      <div class="modal-body px-0">
        <VYoutubeVideo v-if="open" :url="props.seller.profileVideo" />
      </div>
    </VModal>
  </div>
</template>

<style lang="scss">
@import "./SellerPresentationCard.scss";
</style>
